import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBankReconciliationImport,
  listBankReconciliationCandidates,
  listBankReconciliationImports,
  listBankReconciliationTransactions,
  matchBankReconciliationTransaction,
  reviewBankReconciliationTransaction,
  unmatchBankReconciliationTransaction,
  unreviewBankReconciliationTransaction,
  type BankReconciliationImportInput,
} from "@workspace/api-client-react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Check,
  CircleAlert,
  Clock3,
  FileSpreadsheet,
  Landmark,
  Loader2,
  Link2,
  Unlink,
  Upload,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  columnLabel,
  parseBankStatementRows,
  readBankStatementCsv,
  suggestBankStatementMapping,
  type BankStatementColumnMapping,
  type BankStatementDateFormat,
  type BankStatementCsvTable,
} from "@/lib/bank-statement-csv";

type ImportList = Awaited<ReturnType<typeof listBankReconciliationImports>>;
type StatementImport = ImportList["imports"][number];
type TransactionList = Awaited<ReturnType<typeof listBankReconciliationTransactions>>;
type BankTransaction = TransactionList["transactions"][number];
type CandidateList = Awaited<ReturnType<typeof listBankReconciliationCandidates>>;
type MatchCandidate = CandidateList["candidates"][number];

const IMPORTS_QUERY_KEY = ["bank-reconciliation-imports"];

function formatKobo(value: string): string {
  try {
    const kobo = BigInt(value);
    const negative = kobo < 0n;
    const absolute = negative ? -kobo : kobo;
    const whole = absolute / 100n;
    const fraction = String(absolute % 100n).padStart(2, "0");
    return `${negative ? "−" : ""}₦${new Intl.NumberFormat("en-NG").format(whole)}.${fraction}`;
  } catch {
    return "₦0.00";
  }
}

function formatDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-NG", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function queryErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function statusBadge(status: string) {
  if (status === "matched") {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Matched</Badge>;
  }
  if (status === "reviewed") {
    return <Badge variant="secondary">Reviewed with note</Badge>;
  }
  return <Badge variant="outline">Unmatched</Badge>;
}

export default function BankReconciliation() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedImportId, setSelectedImportId] = useState<number | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [matchTransaction, setMatchTransaction] = useState<BankTransaction | null>(null);
  const [reviewTransaction, setReviewTransaction] = useState<BankTransaction | null>(null);
  const [historyTransaction, setHistoryTransaction] = useState<BankTransaction | null>(null);
  const [reviewNote, setReviewNote] = useState("");

  const [accountLabel, setAccountLabel] = useState("");
  const [statementFile, setStatementFile] = useState<File | null>(null);
  const [csvTable, setCsvTable] = useState<BankStatementCsvTable | null>(null);
  const [columnMapping, setColumnMapping] = useState<BankStatementColumnMapping | null>(null);
  const [dateFormat, setDateFormat] = useState<BankStatementDateFormat>("dmy");
  const [fileError, setFileError] = useState("");

  const importsQuery = useQuery({
    queryKey: IMPORTS_QUERY_KEY,
    queryFn: listBankReconciliationImports,
  });
  const imports = importsQuery.data?.imports ?? [];
  const selectedImport = imports.find((row) => row.id === selectedImportId) ?? null;

  useEffect(() => {
    if (selectedImportId === null && imports.length > 0) {
      setSelectedImportId(imports[0].id);
      return;
    }
    if (selectedImportId !== null && imports.length > 0 && !imports.some((row) => row.id === selectedImportId)) {
      setSelectedImportId(imports[0].id);
    }
  }, [imports, selectedImportId]);

  const transactionsQuery = useQuery({
    queryKey: ["bank-reconciliation-transactions", selectedImportId],
    queryFn: () => listBankReconciliationTransactions(selectedImportId!),
    enabled: selectedImportId !== null,
  });
  const transactions = transactionsQuery.data?.transactions ?? [];

  const candidatesQuery = useQuery({
    queryKey: ["bank-reconciliation-candidates", matchTransaction?.id],
    queryFn: () => listBankReconciliationCandidates(matchTransaction!.id),
    enabled: matchTransaction !== null,
  });

  const parsedCsv = useMemo(() => {
    if (!csvTable || !columnMapping) return null;
    return parseBankStatementRows(csvTable.headers, csvTable.rows, columnMapping, dateFormat);
  }, [csvTable, columnMapping, dateFormat]);

  const invalidateStatementData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ["bank-reconciliation-transactions", selectedImportId] }),
      queryClient.invalidateQueries({ queryKey: ["bank-reconciliation-candidates"] }),
    ]);
  };

  const importMutation = useMutation<
    Awaited<ReturnType<typeof createBankReconciliationImport>>,
    Error,
    BankReconciliationImportInput
  >({
    mutationFn: (payload) => createBankReconciliationImport(payload),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: IMPORTS_QUERY_KEY });
      setSelectedImportId(created.id);
      setImportDialogOpen(false);
      setAccountLabel("");
      setStatementFile(null);
      setCsvTable(null);
      setColumnMapping(null);
      setFileError("");
      toast({ title: "Statement imported", description: `${created.transactionCount} bank lines are ready to reconcile.` });
    },
    onError: (error) => {
      toast({
        title: "Could not import statement",
        description: queryErrorMessage(error, "Check the file and try again."),
        variant: "destructive",
      });
    },
  });

  const matchMutation = useMutation({
    mutationFn: ({ transactionId, candidate }: { transactionId: number; candidate: MatchCandidate }) =>
      matchBankReconciliationTransaction(transactionId, {
        sourceType: candidate.sourceType,
        sourceId: candidate.sourceId,
      }),
    onSuccess: async () => {
      await invalidateStatementData();
      setMatchTransaction(null);
      toast({ title: "Bank line matched", description: "The original RetailPulse record was not changed." });
    },
    onError: (error) => toast({
      title: "Could not match bank line",
      description: queryErrorMessage(error, "Refresh the statement and try again."),
      variant: "destructive",
    }),
  });

  const unmatchMutation = useMutation({
    mutationFn: (transactionId: number) => unmatchBankReconciliationTransaction(transactionId),
    onSuccess: async () => {
      await invalidateStatementData();
      toast({ title: "Match removed", description: "The bank line is back in the unmatched queue." });
    },
    onError: (error) => toast({
      title: "Could not remove match",
      description: queryErrorMessage(error, "Refresh the statement and try again."),
      variant: "destructive",
    }),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ transactionId, note }: { transactionId: number; note: string }) =>
      reviewBankReconciliationTransaction(transactionId, { note }),
    onSuccess: async () => {
      await invalidateStatementData();
      setReviewTransaction(null);
      setReviewNote("");
      toast({ title: "Review note saved", description: "No payment or accounting entry was created." });
    },
    onError: (error) => toast({
      title: "Could not save review note",
      description: queryErrorMessage(error, "Refresh the statement and try again."),
      variant: "destructive",
    }),
  });

  const unreviewMutation = useMutation({
    mutationFn: (transactionId: number) => unreviewBankReconciliationTransaction(transactionId),
    onSuccess: invalidateStatementData,
    onError: (error) => toast({
      title: "Could not return line to the queue",
      description: queryErrorMessage(error, "Refresh the statement and try again."),
      variant: "destructive",
    }),
  });

  const totals = useMemo(() => transactions.reduce(
    (result, transaction) => {
      const amount = BigInt(transaction.amountKobo);
      if (amount > 0n) result.credit += amount;
      else result.debit += -amount;
      if (transaction.status === "matched") result.matched++;
      if (transaction.status === "reviewed") result.reviewed++;
      if (transaction.status === "unmatched") result.unmatched++;
      return result;
    },
    { credit: 0n, debit: 0n, matched: 0, reviewed: 0, unmatched: 0 },
  ), [transactions]);

  const resetImportDialog = () => {
    setAccountLabel("");
    setStatementFile(null);
    setCsvTable(null);
    setColumnMapping(null);
    setFileError("");
    setDateFormat("dmy");
  };

  const handleFileChange = async (file: File | null) => {
    setStatementFile(file);
    setCsvTable(null);
    setColumnMapping(null);
    setFileError("");
    if (!file) return;
    try {
      const parsed = await readBankStatementCsv(file);
      setCsvTable(parsed);
      setColumnMapping(suggestBankStatementMapping(parsed.headers));
    } catch (error) {
      setFileError(queryErrorMessage(error, "Could not read this CSV file."));
    }
  };

  const submitImport = () => {
    if (!statementFile || !parsedCsv || parsedCsv.errors.length > 0) return;
    if (accountLabel.trim().length < 2) {
      setFileError("Enter a short account label such as “Main account”. Do not enter the bank account number.");
      return;
    }
    if (/\d{7,}/.test(accountLabel)) {
      setFileError("Use an account label, not a bank account number.");
      return;
    }

    importMutation.mutate({
      accountLabel: accountLabel.trim(),
      fileName: statementFile.name,
      transactions: parsedCsv.transactions,
    });
  };

  const openReview = (transaction: BankTransaction) => {
    setReviewTransaction(transaction);
    setReviewNote("");
  };

  if (importsQuery.isLoading) {
    return <div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-medium text-primary">
            <Landmark className="h-4 w-4" />
            Accounting
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Bank reconciliation</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Import a bank CSV, match exact transactions, and document lines that need manual review.
            Matching never edits sales, expenses, payments, or payout status.
          </p>
        </div>
        <Button onClick={() => setImportDialogOpen(true)}>
          <Upload className="mr-2 h-4 w-4" />
          Import bank CSV
        </Button>
      </div>

      {importsQuery.isError && (
        <Card className="border-destructive/40">
          <CardContent className="flex items-center gap-3 py-5 text-sm text-destructive">
            <CircleAlert className="h-5 w-5 shrink-0" />
            {queryErrorMessage(importsQuery.error, "Could not load bank statements.")}
          </CardContent>
        </Card>
      )}

      {imports.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center px-6 py-14 text-center">
            <div className="mb-4 rounded-full bg-primary/10 p-4 text-primary">
              <FileSpreadsheet className="h-7 w-7" />
            </div>
            <h2 className="text-lg font-semibold">No statements imported yet</h2>
            <p className="mt-2 max-w-lg text-sm text-muted-foreground">
              Start with a CSV exported from your bank. Use NGN amounts; the original file is not stored.
            </p>
            <Button className="mt-5" onClick={() => setImportDialogOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              Import first statement
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-5 xl:grid-cols-[300px_minmax(0,1fr)]">
          <Card className="h-fit">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Imported statements</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {imports.map((statement) => (
                <button
                  key={statement.id}
                  type="button"
                  onClick={() => setSelectedImportId(statement.id)}
                  className={`w-full rounded-lg border p-3 text-left transition-colors ${
                    selectedImportId === statement.id
                      ? "border-primary bg-primary/5"
                      : "border-border hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{statement.accountLabel}</span>
                    {statement.unmatchedCount > 0
                      ? <Badge variant="outline">{statement.unmatchedCount} open</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Clear</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {formatDate(statement.statementStart)} – {formatDate(statement.statementEnd)}
                  </div>
                  <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
                    <span>{statement.transactionCount} lines</span>
                    <span>{statement.matchedCount} matched · {statement.reviewedCount} reviewed</span>
                  </div>
                </button>
              ))}
              {imports.length >= 50 && (
                <p className="px-1 pt-1 text-xs text-muted-foreground">Showing the 50 most recently imported statements.</p>
              )}
            </CardContent>
          </Card>

          <div className="min-w-0 space-y-4">
            {selectedImport && (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <SummaryCard label="Statement lines" value={String(selectedImport.transactionCount)} />
                  <SummaryCard label="Matched" value={String(totals.matched)} tone="green" />
                  <SummaryCard label="Reviewed" value={String(totals.reviewed)} tone="blue" />
                  <SummaryCard label="Still unmatched" value={String(totals.unmatched)} tone={totals.unmatched ? "amber" : "green"} />
                </div>

                <Card>
                  <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                    <div className="min-w-0">
                      <CardTitle className="truncate text-base">{selectedImport.accountLabel}</CardTitle>
                      <p className="mt-1 truncate text-xs text-muted-foreground">
                        {selectedImport.fileName} · {formatDate(selectedImport.statementStart)} – {formatDate(selectedImport.statementEnd)}
                      </p>
                    </div>
                    <Badge variant={totals.unmatched === 0 ? "default" : "secondary"}>
                      {totals.unmatched === 0 ? "Reviewed" : "In progress"}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4 grid gap-3 rounded-lg bg-muted/40 p-3 sm:grid-cols-2">
                      <div className="flex items-center gap-2 text-sm">
                        <ArrowDownLeft className="h-4 w-4 text-emerald-600" />
                        <span className="text-muted-foreground">Credits</span>
                        <span className="ml-auto font-semibold">{formatKobo(totals.credit.toString())}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <ArrowUpRight className="h-4 w-4 text-rose-600" />
                        <span className="text-muted-foreground">Debits</span>
                        <span className="ml-auto font-semibold">{formatKobo(totals.debit.toString())}</span>
                      </div>
                    </div>

                    {transactionsQuery.isLoading ? (
                      <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                    ) : transactionsQuery.isError ? (
                      <div className="py-8 text-center text-sm text-destructive">
                        {queryErrorMessage(transactionsQuery.error, "Could not load statement lines.")}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[760px] text-sm">
                          <thead>
                            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                              <th className="px-3 py-3 font-medium">Date / description</th>
                              <th className="px-3 py-3 font-medium">Reference</th>
                              <th className="px-3 py-3 text-right font-medium">Amount</th>
                              <th className="px-3 py-3 font-medium">Status</th>
                              <th className="px-3 py-3 text-right font-medium">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((transaction) => (
                              <tr key={transaction.id} className="border-b last:border-0">
                                <td className="max-w-[320px] px-3 py-3 align-top">
                                  <div className="font-medium">{formatDate(transaction.transactionDate)}</div>
                                  <div className="mt-0.5 truncate text-xs text-muted-foreground" title={transaction.description}>
                                    {transaction.description}
                                  </div>
                                  {transaction.status === "matched" && transaction.matchedSourceLabel && (
                                    <div className="mt-1 flex items-center gap-1 text-xs text-emerald-700">
                                      <Link2 className="h-3 w-3" />
                                      {transaction.matchedSourceType}: {transaction.matchedSourceLabel}
                                    </div>
                                  )}
                                  {transaction.status === "reviewed" && transaction.reviewNote && (
                                    <div className="mt-1 text-xs text-muted-foreground">Note: {transaction.reviewNote}</div>
                                  )}
                                </td>
                                <td className="max-w-[180px] truncate px-3 py-3 align-top text-xs text-muted-foreground" title={transaction.reference ?? ""}>
                                  {transaction.reference || "—"}
                                </td>
                                <td className={`whitespace-nowrap px-3 py-3 text-right align-top font-semibold ${
                                  BigInt(transaction.amountKobo) > 0n ? "text-emerald-700" : "text-rose-700"
                                }`}>
                                  {formatKobo(transaction.amountKobo)}
                                </td>
                                <td className="px-3 py-3 align-top">
                                  {statusBadge(transaction.status)}
                                  {transaction.matchHistory.length > 0 && (
                                    <button
                                      type="button"
                                      className="mt-1 flex items-center gap-1 text-xs text-muted-foreground underline-offset-2 hover:underline"
                                      onClick={() => setHistoryTransaction(transaction)}
                                    >
                                      <Clock3 className="h-3 w-3" />
                                      History ({transaction.matchHistory.length})
                                    </button>
                                  )}
                                </td>
                                <td className="px-3 py-3 text-right align-top">
                                  {transaction.status === "unmatched" ? (
                                    <div className="flex justify-end gap-2">
                                      <Button size="sm" variant="outline" onClick={() => setMatchTransaction(transaction)}>
                                        <Link2 className="mr-1.5 h-3.5 w-3.5" /> Match
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => openReview(transaction)}>
                                        Review
                                      </Button>
                                    </div>
                                  ) : transaction.status === "matched" ? (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={unmatchMutation.isPending}
                                      onClick={() => unmatchMutation.mutate(transaction.id)}
                                    >
                                      <Unlink className="mr-1.5 h-3.5 w-3.5" /> Unmatch
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      disabled={unreviewMutation.isPending}
                                      onClick={() => unreviewMutation.mutate(transaction.id)}
                                    >
                                      Return to queue
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                            {transactions.length === 0 && (
                              <tr><td colSpan={5} className="py-12 text-center text-sm text-muted-foreground">No statement lines found.</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
            {!selectedImport && imports.length > 0 && (
              <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">Choose a statement to continue.</CardContent></Card>
            )}
          </div>
        </div>
      )}

      <Dialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          setImportDialogOpen(open);
          if (!open) resetImportDialog();
        }}
      >
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import a bank statement CSV</DialogTitle>
            <DialogDescription>
              The file is parsed in your browser. RetailPulse stores normalized statement lines, not the uploaded file or a full bank account number.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="bank-account-label">Account label</Label>
                <Input
                  id="bank-account-label"
                  value={accountLabel}
                  onChange={(event) => setAccountLabel(event.target.value)}
                  placeholder="Main account"
                  maxLength={80}
                />
                <p className="text-xs text-muted-foreground">Use a label only. Do not enter the account number.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank-statement-file">CSV file</Label>
                <Input
                  id="bank-statement-file"
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => void handleFileChange(event.target.files?.[0] ?? null)}
                />
                <p className="text-xs text-muted-foreground">NGN statements only · up to 5 MB and 2,000 transaction rows.</p>
              </div>
            </div>

            {csvTable && columnMapping && (
              <div className="space-y-4 rounded-xl border p-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-semibold">Match the CSV columns</h3>
                    <p className="text-xs text-muted-foreground">Check the detected columns and date format before importing.</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="bank-date-format" className="text-xs">Date format</Label>
                    <select
                      id="bank-date-format"
                      className="h-9 rounded-md border bg-background px-2 text-sm"
                      value={dateFormat}
                      onChange={(event) => setDateFormat(event.target.value as BankStatementDateFormat)}
                    >
                      <option value="dmy">DD/MM/YYYY</option>
                      <option value="mdy">MM/DD/YYYY</option>
                    </select>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  <ColumnSelect
                    label="Transaction date"
                    headers={csvTable.headers}
                    value={columnMapping.date}
                    onChange={(value) => setColumnMapping({ ...columnMapping, date: value })}
                  />
                  <ColumnSelect
                    label="Description"
                    headers={csvTable.headers}
                    value={columnMapping.description}
                    onChange={(value) => setColumnMapping({ ...columnMapping, description: value })}
                  />
                  <ColumnSelect
                    label="Reference (optional)"
                    headers={csvTable.headers}
                    value={columnMapping.reference}
                    optional
                    onChange={(value) => setColumnMapping({ ...columnMapping, reference: value })}
                  />
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="bank-amount-mode">Amount columns</Label>
                    <select
                      id="bank-amount-mode"
                      className="h-9 w-full rounded-md border bg-background px-3 text-sm sm:max-w-xs"
                      value={columnMapping.amountMode}
                      onChange={(event) => setColumnMapping({
                        ...columnMapping,
                        amountMode: event.target.value as BankStatementColumnMapping["amountMode"],
                      })}
                    >
                      <option value="single">One signed amount column</option>
                      <option value="debit-credit">Separate debit and credit columns</option>
                    </select>
                  </div>
                  {columnMapping.amountMode === "single" ? (
                    <ColumnSelect
                      label="Signed amount"
                      headers={csvTable.headers}
                      value={columnMapping.amount}
                      onChange={(value) => setColumnMapping({ ...columnMapping, amount: value })}
                    />
                  ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <ColumnSelect
                        label="Debit"
                        headers={csvTable.headers}
                        value={columnMapping.debit}
                        onChange={(value) => setColumnMapping({ ...columnMapping, debit: value })}
                      />
                      <ColumnSelect
                        label="Credit"
                        headers={csvTable.headers}
                        value={columnMapping.credit}
                        onChange={(value) => setColumnMapping({ ...columnMapping, credit: value })}
                      />
                    </div>
                  )}
                </div>

                {parsedCsv && (
                  <>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span>{parsedCsv.transactions.length} valid lines</span>
                      {parsedCsv.skippedRows > 0 && <span>{parsedCsv.skippedRows} blank or non-transaction rows skipped</span>}
                      {parsedCsv.errors.length > 0 && <span className="font-medium text-destructive">{parsedCsv.errors.length} rows need correction</span>}
                    </div>
                    {parsedCsv.errors.length > 0 && (
                      <div className="max-h-28 overflow-y-auto rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
                        {parsedCsv.errors.slice(0, 8).map((error) => (
                          <div key={`${error.rowNumber}-${error.message}`}>Row {error.rowNumber}: {error.message}</div>
                        ))}
                        {parsedCsv.errors.length > 8 && <div className="mt-1">And {parsedCsv.errors.length - 8} more…</div>}
                      </div>
                    )}
                    {parsedCsv.transactions.length > 0 && (
                      <div className="overflow-x-auto rounded-lg border">
                        <table className="w-full min-w-[560px] text-xs">
                          <thead className="bg-muted/50">
                            <tr className="text-left text-muted-foreground">
                              <th className="px-3 py-2">CSV row</th>
                              <th className="px-3 py-2">Date</th>
                              <th className="px-3 py-2">Description</th>
                              <th className="px-3 py-2 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {parsedCsv.transactions.slice(0, 5).map((transaction) => (
                              <tr key={transaction.rowNumber} className="border-t">
                                <td className="px-3 py-2">{transaction.rowNumber}</td>
                                <td className="px-3 py-2">{formatDate(transaction.transactionDate)}</td>
                                <td className="max-w-[220px] truncate px-3 py-2">{transaction.description}</td>
                                <td className="whitespace-nowrap px-3 py-2 text-right">{formatKobo(transaction.amountKobo)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {parsedCsv.transactions.length > 5 && (
                          <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                            Showing 5 of {parsedCsv.transactions.length} valid lines.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {fileError && <p role="alert" className="text-sm text-destructive">{fileError}</p>}
            <p className="text-xs text-muted-foreground">
              Credits are positive and debits are negative. RetailPulse requires an exact amount and a transaction date within seven days to offer a match.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setImportDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={submitImport}
              disabled={!statementFile || !parsedCsv || parsedCsv.errors.length > 0 || importMutation.isPending}
            >
              {importMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Import {parsedCsv?.transactions.length ?? 0} lines
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={matchTransaction !== null}
        onOpenChange={(open) => {
          if (!open) setMatchTransaction(null);
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Match bank transaction</DialogTitle>
            <DialogDescription>
              Only exact-amount RetailPulse records within seven days are offered. Matching links records; it does not post or change money.
            </DialogDescription>
          </DialogHeader>
          {matchTransaction && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold">{matchTransaction.description}</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {formatDate(matchTransaction.transactionDate)}{matchTransaction.reference ? ` · ${matchTransaction.reference}` : ""}
                    </div>
                  </div>
                  <div className={`whitespace-nowrap text-sm font-semibold ${
                    BigInt(matchTransaction.amountKobo) > 0n ? "text-emerald-700" : "text-rose-700"
                  }`}>
                    {formatKobo(matchTransaction.amountKobo)}
                  </div>
                </div>
              </div>

              {candidatesQuery.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
              ) : candidatesQuery.isError ? (
                <p className="py-5 text-sm text-destructive">Could not load match candidates. Close and try again.</p>
              ) : candidatesQuery.data?.candidates.length ? (
                <div className="space-y-2">
                  {candidatesQuery.data.candidates.map((candidate) => (
                    <div key={`${candidate.sourceType}:${candidate.sourceId}`} className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{candidate.label}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {candidate.sourceType} · {formatDate(candidate.date)}{candidate.details ? ` · ${candidate.details}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:justify-end">
                        <span className="whitespace-nowrap text-sm font-semibold">{formatKobo(candidate.amountKobo)}</span>
                        <Button
                          size="sm"
                          disabled={matchMutation.isPending}
                          onClick={() => matchMutation.mutate({ transactionId: matchTransaction.id, candidate })}
                        >
                          <Link2 className="mr-1.5 h-3.5 w-3.5" /> Match
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed px-4 py-8 text-center">
                  <CircleAlert className="mx-auto h-5 w-5 text-muted-foreground" />
                  <p className="mt-2 text-sm font-medium">No exact one-to-one match found</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    For grouped settlements or bank charges, close this window and add a review note instead.
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={reviewTransaction !== null}
        onOpenChange={(open) => {
          if (!open) {
            setReviewTransaction(null);
            setReviewNote("");
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review bank line</DialogTitle>
            <DialogDescription>
              Use this for grouped deposits, fees, or other lines that cannot be matched one-to-one.
            </DialogDescription>
          </DialogHeader>
          {reviewTransaction && (
            <div className="space-y-4">
              <div className="rounded-lg bg-muted/50 p-3 text-sm">
                <div className="font-medium">{reviewTransaction.description}</div>
                <div className="mt-1 text-xs text-muted-foreground">{formatDate(reviewTransaction.transactionDate)}</div>
                <div className="mt-2 font-semibold">{formatKobo(reviewTransaction.amountKobo)}</div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bank-review-note">Review note</Label>
                <Textarea
                  id="bank-review-note"
                  value={reviewNote}
                  onChange={(event) => setReviewNote(event.target.value)}
                  placeholder="For example: Paystack payout batch reference …"
                  maxLength={500}
                  rows={3}
                />
              </div>
              <p className="text-xs text-amber-700">
                This records a note only. It does not create a payment or expense, or mark a Paystack payout as settled.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setReviewTransaction(null)}>Cancel</Button>
                <Button
                  disabled={reviewNote.trim().length < 3 || reviewMutation.isPending}
                  onClick={() => reviewMutation.mutate({
                    transactionId: reviewTransaction.id,
                    note: reviewNote.trim(),
                  })}
                >
                  {reviewMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
                  Mark reviewed
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={historyTransaction !== null}
        onOpenChange={(open) => {
          if (!open) setHistoryTransaction(null);
        }}
      >
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bank line history</DialogTitle>
            <DialogDescription>
              {historyTransaction?.description}
            </DialogDescription>
          </DialogHeader>
          {historyTransaction && (
            <ol className="space-y-3">
              {historyTransaction.matchHistory.map((entry, index) => (
                <li key={`${entry.at}-${index}`} className="relative border-l pl-4">
                  <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-primary" />
                  <div className="text-sm font-medium">
                    {entry.action === "matched" && `Matched to ${entry.sourceType}`}
                    {entry.action === "unmatched" && `Removed match${entry.sourceType ? ` to ${entry.sourceType}` : ""}`}
                    {entry.action === "reviewed" && "Marked reviewed"}
                    {entry.action === "unreviewed" && "Returned to unmatched queue"}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {new Intl.DateTimeFormat("en-NG", {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: "Africa/Lagos",
                    }).format(new Date(entry.at))}
                    {" · "}Staff #{entry.actorId}
                  </div>
                  {entry.sourceLabel && <div className="mt-1 text-xs">{entry.sourceLabel}</div>}
                  {entry.note && <div className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{entry.note}</div>}
                </li>
              ))}
            </ol>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, value, tone = "default" }: { label: string; value: string; tone?: "default" | "green" | "blue" | "amber" }) {
  const toneClass = tone === "green"
    ? "text-emerald-700"
    : tone === "blue"
      ? "text-blue-700"
      : tone === "amber"
        ? "text-amber-700"
        : "text-foreground";
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function ColumnSelect({
  label,
  headers,
  value,
  onChange,
  optional = false,
}: {
  label: string;
  headers: string[];
  value: number;
  onChange: (value: number) => void;
  optional?: boolean;
}) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <select
        className="h-9 w-full rounded-md border bg-background px-3 text-sm"
        value={String(value)}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {optional && <option value="-1">Skip</option>}
        {!optional && <option value="-1">Select a column</option>}
        {headers.map((header, index) => (
          <option key={`${index}-${header}`} value={String(index)}>
            {header || `Column ${index + 1}`}
          </option>
        ))}
      </select>
      <span className="sr-only">{columnLabel(headers, value)}</span>
    </div>
  );
}
